import { useEffect, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { CheckCircle2, ChevronLeft, Loader2 } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import StoreHeader from '@/components/store/StoreHeader'
import StoreFooter from '@/components/store/StoreFooter'
import { storeForm } from '@/components/store/storeFormStyles'
import { api, type StorefrontOrder } from '@/lib/api'
import { formatOrderDate, orderQrPayload, orderStatusBadge } from '@/lib/orders'

const money = (n: number) => `$${n.toFixed(2)}`

/**
 * `/order/:orderNumber`: the thank-you confirmation right after checkout (checkout replaces itself
 * with `/` in history first, so browser back lands on home), or order details when opened from My orders.
 */
export default function OrderConfirmationPage() {
  const { orderNumber = '' } = useParams()
  const { state } = useLocation() as { state: { order?: StorefrontOrder } | null }
  const fromCheckout = state?.order?.order_number === orderNumber ? state.order : null
  // `undefined` while loading from the server; `null` when it isn't this customer's order (or not signed in).
  const [order, setOrder] = useState<StorefrontOrder | null | undefined>(fromCheckout ?? undefined)

  useEffect(() => {
    if (fromCheckout) return
    let cancelled = false
    api.orders
      .get(orderNumber)
      .then((res) => {
        if (!cancelled) setOrder(res.success && res.data ? res.data : null)
      })
      .catch(() => {
        if (!cancelled) setOrder(null)
      })
    return () => {
      cancelled = true
    }
  }, [orderNumber, fromCheckout])

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-orange-50 via-stone-100 to-stone-300/60">
      <StoreHeader />

      <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-4 pb-6 pt-4 sm:px-6 lg:px-8">
        {order === undefined ? (
          <div className="flex justify-center py-10 text-stone-400" aria-label="Loading order">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : !order ? (
          <div className="rounded-2xl border border-stone-200 bg-white p-6 text-center shadow-sm">
            <p className="text-sm font-semibold text-stone-900">Order not found</p>
            <p className="mt-1 text-xs text-stone-500 sm:text-sm">
              We couldn&apos;t find order {orderNumber} on your account. Make sure you&apos;re signed in.
            </p>
            <Link to="/" replace className={`mt-4 ${storeForm.submit}`}>
              Go to home
            </Link>
          </div>
        ) : (
          <>
            {!fromCheckout && (
              <Link
                to="/orders"
                className="mb-2 inline-flex items-center gap-0.5 self-start text-xs font-medium text-stone-600 hover:text-primary-700 sm:text-sm"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
                My orders
              </Link>
            )}
            <div
              className={`overflow-hidden rounded-2xl border bg-white shadow-sm ${fromCheckout ? 'border-emerald-200' : 'border-stone-200'}`}
            >
              {fromCheckout ? (
                <div className="bg-emerald-50/90 px-4 py-4 sm:px-6 sm:py-5">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" aria-hidden />
                    <h1 className="text-base font-semibold text-emerald-900 sm:text-lg">
                      Thank you, we have received your order.
                    </h1>
                  </div>
                  <p className="mt-1.5 text-xs leading-relaxed text-stone-700 sm:text-sm">
                    Our team will send a confirmation email or text message once the order is ready for pickup.
                    Please pay the invoice at the store.
                  </p>
                </div>
              ) : (
                <div className="px-4 py-4 sm:px-6 sm:py-5">
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-base font-semibold text-stone-900 sm:text-lg">Order details</h1>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold sm:text-xs ${orderStatusBadge(order.order_status).tone}`}
                    >
                      {orderStatusBadge(order.order_status).label}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-stone-500 sm:text-sm">
                    Placed {formatOrderDate(order.placed_at)} · Pay at the store when you pick up.
                  </p>
                </div>
              )}

              <div className="flex flex-col items-center gap-2 border-y border-stone-100 px-4 py-5 sm:px-6">
                <p className={storeForm.fieldLabel}>Order number</p>
                <p className="font-mono text-lg font-bold tracking-wider text-stone-900 sm:text-xl">{order.order_number}</p>
                <div className="rounded-xl border border-stone-200 bg-white p-3">
                  <QRCodeSVG
                    value={orderQrPayload(order)}
                    size={168}
                    level="M"
                    marginSize={0}
                    title={`Order ${order.order_number} QR code`}
                  />
                </div>
                <p className={storeForm.hint}>Show this code at the counter when you pick up.</p>
              </div>

              <div className="px-4 py-4 sm:px-6">
                <ul className="divide-y divide-stone-100">
                  {order.lines.map((l, i) => (
                    <li key={l.inventory_id ?? i} className="flex items-baseline gap-2 py-1.5 text-xs sm:text-sm">
                      <span className="w-7 shrink-0 tabular-nums text-stone-500">{l.quantity}×</span>
                      <span className="min-w-0 flex-1 truncate text-stone-800">{l.name}</span>
                      <span className="shrink-0 tabular-nums text-stone-900">{money(l.line_total)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-2 space-y-1 border-t border-stone-200 pt-2 text-xs text-stone-600 sm:text-sm">
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span className="tabular-nums">{money(order.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sales tax ({order.tax_rate_label})</span>
                    <span className="tabular-nums">{money(order.tax)}</span>
                  </div>
                  <div className="flex justify-between pt-1 text-sm font-bold text-stone-900 sm:text-base">
                    <span>
                      Total <span className="font-normal text-stone-500">(incl. tax)</span>
                    </span>
                    <span className="tabular-nums">{money(order.total)}</span>
                  </div>
                </div>

                <p className="mt-3 text-xs text-stone-600 sm:text-sm">
                  <span className="font-medium text-stone-700">On file:</span>{' '}
                  {[`${order.customer.first_name} ${order.customer.last_name}`.trim(), order.customer.phone, order.customer.email]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                {order.notes.trim() ? (
                  <p className="mt-1 text-xs text-stone-600 sm:text-sm">
                    <span className="font-medium text-stone-700">Notes:</span> {order.notes.trim()}
                  </p>
                ) : null}

                <Link to="/" replace={!!fromCheckout} className={`mt-4 ${storeForm.submit}`}>
                  Continue shopping
                </Link>
              </div>
            </div>
          </>
        )}

        <StoreFooter detailsClassName="mt-6 sm:mt-12" />
      </main>
    </div>
  )
}
